import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Calendar } from "./ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  SERVICE_OPTIONS,
  TIME_SLOTS,
  FREQUENCIES,
  WEEKDAYS,
  MONTH_DAYS,
  EXPERTISE_DOMAINS,
} from "../lib/content";
import { createBooking } from "../lib/api";
import { Check, ChevronRight, ChevronLeft, CalendarDays, CheckCircle2, MapPin } from "lucide-react";

const empty = {
  child_name: "",
  child_email: "",
  child_phone: "",
  parent_name: "",
  parent_address: "",
  parent_location: "",
  services: [],
  preferred_date: "",
  time_slot: "",
  frequency: "Every week",
  schedule_days: [],
  priority: false,
  expertise_domain: "",
  notes: "",
  consent_given: false,
};

export const BookingDialog = ({ open, onOpenChange, initialPriority = false }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(empty);
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [locating, setLocating] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open && initialPriority) setForm((f) => ({ ...f, priority: true }));
  }, [open, initialPriority]);

  const shareLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Location isn't supported on this device.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        set("parent_location", `https://www.google.com/maps?q=${latitude},${longitude}`);
        toast.success("Live location captured.");
        setLocating(false);
      },
      () => {
        toast.error("Could not get location. Please allow location permission.");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const toggleService = (label) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(label)
        ? f.services.filter((s) => s !== label)
        : [...f.services, label],
    }));

  const reset = () => {
    setForm(empty);
    setDate(null);
    setStep(1);
    setDone(false);
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(reset, 300);
  };

  const step1Valid =
    form.child_name.trim() &&
    form.child_email.trim() &&
    form.child_phone.trim() &&
    form.parent_name.trim() &&
    form.parent_address.trim();
  const step2Valid = form.services.length > 0 && date && form.time_slot;

  const submit = async () => {
    if (!form.consent_given) {
      toast.error("Please provide your consent to proceed.");
      return;
    }
    setSubmitting(true);
    try {
      await createBooking({
        ...form,
        preferred_date: date ? date.toISOString().split("T")[0] : "",
      });
      setDone(true);
      toast.success("Visit request received. We'll call you shortly.");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        data-testid="booking-dialog"
        className="max-w-2xl bg-brand-cream border-border max-h-[92vh] overflow-y-auto"
      >
        {done ? (
          <div className="py-10 text-center" data-testid="booking-success">
            <div className="mx-auto h-16 w-16 rounded-full bg-brand-sage/20 flex items-center justify-center mb-6">
              <CheckCircle2 className="text-brand-sage" size={34} />
            </div>
            <h3 className="font-serif text-3xl text-brand-ink mb-3">
              Thank you, {form.child_name.split(" ")[0]}.
            </h3>
            <p className="text-brand-ink/70 max-w-md mx-auto mb-8">
              Your request to visit {form.parent_name} has been received. Our
              Jaipur team will call you within a few hours to confirm the visit
              and time slot.
            </p>
            <Button
              data-testid="booking-close-btn"
              onClick={() => close(false)}
              className="rounded-full bg-brand hover:bg-brand-hover text-white px-8"
            >
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-3xl font-light text-brand-ink">
                Request a home visit
              </DialogTitle>
              <DialogDescription className="text-brand-ink/60">
                Step {step} of 3 —{" "}
                {step === 1
                  ? "About you & your parents"
                  : step === 2
                  ? "Care & schedule"
                  : "Your consent"}
              </DialogDescription>
            </DialogHeader>

            {/* progress */}
            <div className="flex gap-2 mb-2">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${
                    s <= step ? "bg-brand" : "bg-border"
                  }`}
                />
              ))}
            </div>

            {/* STEP 1 */}
            {step === 1 && (
              <div className="space-y-4 py-2">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Your name">
                    <Input
                      data-testid="input-child-name"
                      value={form.child_name}
                      onChange={(e) => set("child_name", e.target.value)}
                      placeholder="e.g. Rohan Mehta"
                    />
                  </Field>
                  <Field label="Your phone (WhatsApp)">
                    <Input
                      data-testid="input-child-phone"
                      value={form.child_phone}
                      onChange={(e) => set("child_phone", e.target.value)}
                      placeholder="+91 ..."
                    />
                  </Field>
                </div>
                <Field label="Your email">
                  <Input
                    data-testid="input-child-email"
                    type="email"
                    value={form.child_email}
                    onChange={(e) => set("child_email", e.target.value)}
                    placeholder="you@email.com"
                  />
                </Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Parent's name">
                    <Input
                      data-testid="input-parent-name"
                      value={form.parent_name}
                      onChange={(e) => set("parent_name", e.target.value)}
                      placeholder="e.g. Sita Mehta"
                    />
                  </Field>
                  <Field label="Home address in Jaipur">
                    <Input
                      data-testid="input-parent-address"
                      value={form.parent_address}
                      onChange={(e) => set("parent_address", e.target.value)}
                      placeholder="Home address / locality"
                    />
                  </Field>
                </div>

                <div className="rounded-xl border border-border bg-white p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-brand-ink flex items-center gap-1.5">
                      <MapPin size={15} className="text-brand" /> Parents' live location
                    </p>
                    <p className="text-xs text-brand-ink/50 mt-0.5">
                      {form.parent_location
                        ? "Location captured ✓ — helps our representative reach them faster"
                        : "Optional — share the exact location for faster visits"}
                    </p>
                  </div>
                  {form.parent_location ? (
                    <a
                      href={form.parent_location}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="location-link"
                      className="text-sm text-brand font-semibold whitespace-nowrap"
                    >
                      View map
                    </a>
                  ) : (
                    <button
                      type="button"
                      data-testid="share-location-btn"
                      onClick={shareLocation}
                      disabled={locating}
                      className="inline-flex items-center gap-1.5 rounded-full bg-brand-ink text-white text-sm px-4 py-2 whitespace-nowrap active:scale-95 transition-transform"
                    >
                      <MapPin size={14} /> {locating ? "Locating..." : "Share location"}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* STEP 2 */}
            {step === 2 && (
              <div className="space-y-6 py-2">
                <div>
                  <Label className="text-brand-ink font-medium mb-3 block">
                    Which services do they need?
                  </Label>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {SERVICE_OPTIONS.map((s) => {
                      const active = form.services.includes(s);
                      return (
                        <button
                          type="button"
                          key={s}
                          data-testid={`service-option-${s.split(" ")[0].toLowerCase()}`}
                          onClick={() => toggleService(s)}
                          className={`flex items-center gap-2 rounded-xl border px-4 py-3 text-sm text-left transition-colors ${
                            active
                              ? "border-brand bg-brand/5 text-brand-ink"
                              : "border-border bg-white text-brand-ink/70 hover:border-brand/40"
                          }`}
                        >
                          <span
                            className={`h-5 w-5 rounded-md flex items-center justify-center shrink-0 ${
                              active ? "bg-brand text-white" : "bg-brand-sand"
                            }`}
                          >
                            {active && <Check size={13} />}
                          </span>
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-brand-ink font-medium mb-3 block flex items-center gap-2">
                      <CalendarDays size={16} className="text-brand" /> Preferred
                      date
                    </Label>
                    <div className="rounded-xl border border-border bg-white p-2 flex justify-center">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                        data-testid="booking-calendar"
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <Field label="Time slot (as per their comfort)">
                      <Select
                        value={form.time_slot}
                        onValueChange={(v) => set("time_slot", v)}
                      >
                        <SelectTrigger data-testid="select-time-slot">
                          <SelectValue placeholder="Choose a time slot" />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_SLOTS.map((t) => (
                            <SelectItem key={t} value={t}>
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <div>
                      <Label className="text-brand-ink/80 text-sm font-medium mb-1.5 block">
                        How often should we visit?
                      </Label>
                      <div className="grid grid-cols-1 gap-2">
                        {FREQUENCIES.map((f) => (
                          <button
                            type="button"
                            key={f}
                            data-testid={`freq-${f.replace(/\s+/g, "-").toLowerCase()}`}
                            onClick={() =>
                              setForm((p) => ({ ...p, frequency: f, schedule_days: [] }))
                            }
                            className={`rounded-xl border px-4 py-2.5 text-sm text-left transition-colors ${
                              form.frequency === f
                                ? "border-brand bg-brand/5 text-brand-ink font-medium"
                                : "border-border bg-white text-brand-ink/70 hover:border-brand/40"
                            }`}
                          >
                            {f}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {(() => {
                  const isWeekly = form.frequency === "Every week";
                  const cap =
                    form.frequency === "2 days a month"
                      ? 2
                      : form.frequency === "4 days a month"
                      ? 4
                      : 7;
                  const options = isWeekly ? WEEKDAYS : MONTH_DAYS;
                  const label = isWeekly
                    ? "Which day of the week suits you?"
                    : `Select up to ${cap} preferred day(s) of the month`;
                  const toggle = (d) =>
                    setForm((f) => {
                      if (f.schedule_days.includes(d))
                        return { ...f, schedule_days: f.schedule_days.filter((x) => x !== d) };
                      if (f.schedule_days.length >= cap) return f;
                      return { ...f, schedule_days: [...f.schedule_days, d] };
                    });
                  return (
                    <div>
                      <Label className="text-brand-ink font-medium mb-3 block">{label}</Label>
                      <div className="flex flex-wrap gap-2" data-testid="schedule-days">
                        {options.map((d) => {
                          const active = form.schedule_days.includes(d);
                          return (
                            <button
                              type="button"
                              key={d}
                              data-testid={`day-${d}`}
                              onClick={() => toggle(d)}
                              className={`h-9 min-w-[2.25rem] px-3 rounded-full border text-sm transition-colors ${
                                active
                                  ? "border-brand bg-brand text-white"
                                  : "border-border bg-white text-brand-ink/70 hover:border-brand/40"
                              }`}
                            >
                              {d}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-brand-ink/80 text-sm font-medium mb-1.5 block">
                      Doctor's domain of expertise (optional)
                    </Label>
                    <Select value={form.expertise_domain} onValueChange={(v) => set("expertise_domain", v)}>
                      <SelectTrigger data-testid="select-expertise">
                        <SelectValue placeholder="Choose a specialty" />
                      </SelectTrigger>
                      <SelectContent>
                        {EXPERTISE_DOMAINS.map((d) => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <label
                      data-testid="priority-label"
                      className={`w-full flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                        form.priority ? "border-brand bg-brand/10" : "border-border bg-white"
                      }`}
                    >
                      <Checkbox
                        data-testid="priority-checkbox"
                        checked={form.priority}
                        onCheckedChange={(v) => set("priority", !!v)}
                        className="data-[state=checked]:bg-brand data-[state=checked]:border-brand"
                      />
                      <span className="text-sm text-brand-ink leading-tight">
                        <b>Prioritise my booking</b>
                        <span className="block text-xs text-brand-ink/60">Faster scheduling for doctor visits</span>
                      </span>
                    </label>
                  </div>
                </div>

                <Field label="Anything we should know? (optional)">
                  <Textarea
                    data-testid="input-notes"
                    value={form.notes}
                    onChange={(e) => set("notes", e.target.value)}
                    placeholder="Medical conditions, mobility, language spoken..."
                    rows={3}
                  />
                </Field>
              </div>
            )}

            {/* STEP 3 */}
            {step === 3 && (
              <div className="space-y-5 py-2">
                <div className="rounded-2xl bg-white border border-border p-6">
                  <h4 className="font-serif text-xl text-brand-ink mb-4">
                    Please review
                  </h4>
                  <dl className="space-y-2 text-sm">
                    <Row k="You" v={`${form.child_name} · ${form.child_phone}`} />
                    <Row k="Parent" v={`${form.parent_name}, ${form.parent_address}`} />
                    <Row k="Location" v={form.parent_location ? "Live location captured ✓" : "Not shared"} />
                    <Row k="Priority" v={form.priority ? "Yes — prioritised" : "Standard"} />
                    {form.expertise_domain && <Row k="Doctor domain" v={form.expertise_domain} />}
                    <Row k="Services" v={form.services.join(", ") || "—"} />
                    <Row
                      k="Schedule"
                      v={`${date ? date.toDateString() : "—"} · ${form.time_slot || "—"} · ${form.frequency}${form.schedule_days.length ? " · days: " + form.schedule_days.join(", ") : ""}`}
                    />
                  </dl>
                </div>

                <label
                  className="flex items-start gap-3 rounded-2xl bg-brand-sage/10 border border-brand-sage/30 p-5 cursor-pointer"
                  data-testid="consent-label"
                >
                  <Checkbox
                    data-testid="consent-checkbox"
                    checked={form.consent_given}
                    onCheckedChange={(v) => set("consent_given", !!v)}
                    className="mt-0.5 data-[state=checked]:bg-brand data-[state=checked]:border-brand"
                  />
                  <span className="text-sm text-brand-ink/80 leading-relaxed">
                    I am a family member of{" "}
                    <b>{form.parent_name || "the elder"}</b> and I give my
                    consent for an Eldercare24 representative to visit their home
                    in Jaipur to provide the selected services. I understand I
                    can withdraw this consent at any time.
                  </span>
                </label>
              </div>
            )}

            {/* nav */}
            <div className="flex items-center justify-between pt-2">
              {step > 1 ? (
                <Button
                  data-testid="booking-back-btn"
                  variant="ghost"
                  onClick={() => setStep((s) => s - 1)}
                  className="text-brand-ink/70"
                >
                  <ChevronLeft size={18} /> Back
                </Button>
              ) : (
                <span />
              )}

              {step < 3 ? (
                <Button
                  data-testid="booking-next-btn"
                  onClick={() => setStep((s) => s + 1)}
                  disabled={step === 1 ? !step1Valid : !step2Valid}
                  className="rounded-full bg-brand hover:bg-brand-hover text-white px-7"
                >
                  Continue <ChevronRight size={18} />
                </Button>
              ) : (
                <Button
                  data-testid="booking-submit-btn"
                  onClick={submit}
                  disabled={!form.consent_given || submitting}
                  className="rounded-full bg-brand hover:bg-brand-hover text-white px-8"
                >
                  {submitting ? "Sending..." : "Confirm request"}
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-brand-ink/80 text-sm font-medium mb-1.5 block">
      {label}
    </Label>
    {children}
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex gap-3">
    <dt className="w-24 shrink-0 text-brand-ink/50">{k}</dt>
    <dd className="text-brand-ink font-medium">{v}</dd>
  </div>
);
